import { $isElementNode, $isTextNode, RootNode } from "lexical"

/**
 * Determines whether the editor placeholder should be shown based on the root node.
 * Considers non-text decorator content (e.g., raw-html) as non-empty and ignores zero-width spaces.
 */
export function shouldShowPlaceholderFromRoot(root: RootNode): boolean {
  const textContent = root.getTextContent()

  const hasNonEmptyElementChild = root.getChildren().some((child) => {
    // Check for nodes that are always considered non-empty content
    const childType = (child as { getType?: () => string }).getType?.()
    if (childType === "raw-html" || childType === "inline-image" || childType === "question") {
      return true // These nodes are always considered non-empty content
    }

    if ($isElementNode(child)) {
      const isParagraph = childType === "paragraph"
      if (isParagraph) {
        const children = child.getChildren()
        if (children.length === 0) return false
        // Treat paragraph as empty if all children are text nodes with only whitespace/zero-width content
        const allTextEmpty = children.every((n) => {
          if ($isTextNode(n)) {
            const t = n
              .getTextContent()
              .replace(/\u200B/g, "")
              .trim()
            return t.length === 0
          }
          // Non-text child (e.g., decorator like raw-html) => non-empty
          return false
        })
        return !allTextEmpty
      }
      // Non-paragraph element => consider non-empty content exists
      return true
    }
    return false
  })

  const onlyZeroWidth = textContent === "\u200B"
  const noText = textContent === ""

  return !hasNonEmptyElementChild && (noText || onlyZeroWidth)
}
